import {
  ClaimResponseItemAdjudicationCategory,
  ClaimResponseTotalCategory,
  ClaimResponseTypeCode,
  ClaimTypeCode,
  Currency,
  createCodeableConcept,
  createExtensionValue,
  createFhirDateTime,
  createReference,
  ExtensionUrl,
  getFirstCodeFromCodableConcepts,
  valsOrNone,
} from '@quromedical/fhir-common';
import {
  type ClaimResponse,
  type ClaimResponseClaimFinancials,
  type ClaimResponseDetails,
  type ClaimResponseLineItem,
  StatusCode,
} from '@quromedical/healthbridge/client';
import { compact, first } from '@quromedical/utils';
import {
  type ChargeItemDefinitionMoney,
  MONEY_FACTOR,
  mapFhirToMoney,
  mapMoneyToFhir,
} from '../charge-item-definition';
import type { PartialClaim } from '../claim/schema';
import { getValidReference } from '../mappings/reference';
import type { WithId, WithRequired } from '../types';

import type {
  ClaimResponseItemTotal,
  ClaimResponseModelGetResponse,
  ClaimResponseTotal,
} from './schema';

export type ClaimWithLinks = WithRequired<
  Partial<PartialClaim>,
  'patient' | 'type' | 'provider'
>;

const claimToResponseTypeMap: Record<ClaimTypeCode, ClaimResponseTypeCode> = {
  [ClaimTypeCode.Arthroplasty]: ClaimResponseTypeCode.Arthroplasty,
  [ClaimTypeCode.HealingHands]: ClaimResponseTypeCode.HealingHands,
  [ClaimTypeCode.InHospitalMonitoring]:
    ClaimResponseTypeCode.InHospitalMonitoring,
  [ClaimTypeCode.HospitalAtHome]: ClaimResponseTypeCode.HospitalAtHome,
  [ClaimTypeCode.TCP]: ClaimResponseTypeCode.TCP,
  [ClaimTypeCode.RemotePatientMonitoring]:
    ClaimResponseTypeCode.RemotePatientMonitoring,
  [ClaimTypeCode.HospitalAtHomeUpgrade]:
    ClaimResponseTypeCode.HospitalAtHomeUpgrade,
  [ClaimTypeCode.SPHERE]: ClaimResponseTypeCode.SPHERE,
};

const createTraceNumberExtension = createExtensionValue(
  ExtensionUrl.claimResponseItemTraceNumber,
);

const getDisclaimerNotes = (
  num: number,
  disclaimer?: string,
): NoteWithNumber[] => (disclaimer ? [{ number: num, text: disclaimer }] : []);

type NoteWithNumber = WithRequired<fhir4.ClaimResponseProcessNote, 'number'>;

const mapFinancialToMoney = (amount: string) =>
  mapMoneyToFhir({
    currency: Currency.ZAR,
    factor: MONEY_FACTOR,
    value: +amount,
  });

const getClaimResponseTotals = (
  claimFinancials: ClaimResponseClaimFinancials,
): fhir4.ClaimResponseTotal[] => [
  {
    category: createCodeableConcept(
      ClaimResponseTotalCategory.TotalClaimedAmount,
    ),
    amount: mapFinancialToMoney(claimFinancials.TotalClaimedAmt),
  },
  {
    category: createCodeableConcept(
      ClaimResponseTotalCategory.TotalPaidProviderAmount,
    ),
    amount: mapFinancialToMoney(claimFinancials.TotalPaidProvAmt),
  },
  {
    category: createCodeableConcept(
      ClaimResponseTotalCategory.BalanceOutstandingAmount,
    ),
    amount: mapFinancialToMoney(claimFinancials.BalanceOutstanding),
  },
];

const getDispositionNote = (
  claimDetails: ClaimResponseDetails,
): string | undefined =>
  claimDetails.Responses?.map((res) => res.Desc).join('\n');

const queuedCodes = [
  StatusCode.AwaitingResponse,
  StatusCode.ReceivedByHealthbridge,
  StatusCode.ValidatedByHealthbridge,
  StatusCode.ValidatedByFunder,
] as string[];

/*
 * queued = validated, not processed
 * error = error at top level
 * complete = all lines "Processed"
 * partial = some lines "Processed"
 */
const getOutcome = (
  claimDetails: ClaimResponseDetails,
): fhir4.ClaimResponse['outcome'] => {
  // Any error means we have an error result
  const topLevelError = first(claimDetails.Responses)?.['@Type'] === 'Error';
  if (topLevelError) {
    return 'error';
  }

  // Complete if all items are processed
  const isComplete = claimDetails.LineItems.every(
    (line) => line.StatusCode === StatusCode.Processed,
  );

  if (isComplete) {
    return 'complete';
  }

  // Queued if all items are only validated and not handled in any other way
  const isQueued = claimDetails.LineItems.every((line) =>
    queuedCodes.includes(line.StatusCode),
  );

  if (isQueued) {
    return 'queued';
  }

  return 'partial';
};

const createResponseItem = (
  item: ClaimResponseLineItem,
  itemNoteNumbers: number[],
): fhir4.ClaimResponseItem => {
  const itemFinancials = item.LineItemFinancials;

  return {
    extension: item.MALineRefNum
      ? [createTraceNumberExtension(item.MALineRefNum)]
      : undefined,
    // this needs to be used in accordance with lookup via the claim to determine which item it relates to
    itemSequence: +item.UniqueID,
    noteNumber: valsOrNone(itemNoteNumbers),
    adjudication: compact([
      {
        category: createCodeableConcept(
          ClaimResponseItemAdjudicationCategory.BilledAmount,
        ),
        amount: mapFinancialToMoney(itemFinancials.BilledAmt),
      },
      {
        category: createCodeableConcept(
          ClaimResponseItemAdjudicationCategory.MedicalAidPayable,
        ),
        amount: mapFinancialToMoney(itemFinancials.MAPayable),
      },
      itemFinancials.MedicalAidRate
        ? {
            category: createCodeableConcept(
              ClaimResponseItemAdjudicationCategory.MedicalAidRate,
            ),
            amount: mapFinancialToMoney(itemFinancials.MedicalAidRate),
          }
        : undefined,
    ]),
  };
};

/**
 * Create the line items with the links to their associated process notes
 */
const createResponseItems = (
  claimItems: ClaimResponseLineItem[],
  disclaimer?: string,
) => {
  const processNote = getDisclaimerNotes(1, disclaimer);

  const items: fhir4.ClaimResponseItem[] = [];

  for (const item of claimItems) {
    const lastNumber = processNote[processNote.length - 1]?.number || 0;
    const itemNotes = item.Responses.map<NoteWithNumber>((res, index) => ({
      text: `${res.Code} - ${res.Desc}`,
      number: lastNumber + index + 1,
    }));

    const itemNoteNumbers = itemNotes.map((note) => note.number);
    const fhirItem = createResponseItem(item, itemNoteNumbers);

    processNote.push(...itemNotes);
    items.push(fhirItem);
  }

  return { items, processNote };
};

export const mapHealthbridgeResponseToFhir = (
  response: ClaimResponse,
  claim: WithId<ClaimWithLinks>,
  createdDateTime = new Date(),
): fhir4.ClaimResponse => {
  const claimDetails = response.MedicalAidMessage.Claim.Details;
  const typeCode = claimToResponseTypeMap[claim.type];
  const disposition = getDispositionNote(claimDetails);
  const total = getClaimResponseTotals(claimDetails.ClaimFinancials);
  const outcome = getOutcome(claimDetails);
  const type = createCodeableConcept(typeCode);
  const request = createReference('Claim', claim.id);
  const created = createFhirDateTime(createdDateTime);

  const { items, processNote } = createResponseItems(
    claimDetails.LineItems,
    response.MedicalAidMessage.Claim.Disclaimer,
  );

  return {
    resourceType: 'ClaimResponse',
    status: 'active',
    use: 'claim',
    outcome,
    type,
    total,
    created,
    request,
    disposition,
    processNote,
    item: items,
    insurer: claim.provider,
    patient: claim.patient,
  };
};

export const hasClaimLinks = (
  claim: Partial<PartialClaim>,
): claim is ClaimWithLinks => !!(claim.type && claim.patient && claim.provider);

export const getClaimInvoiceNumber = (response: ClaimResponse) =>
  response.MedicalAidMessage.Claim.Transaction.InvoiceNum;

const getTotal = (
  total: fhir4.ClaimResponseTotal[] = [],
): Partial<ClaimResponseTotal> => {
  const splitTotals = total.reduce<
    Partial<Record<ClaimResponseTotalCategory, ChargeItemDefinitionMoney>>
  >((prev, current) => {
    const code = getFirstCodeFromCodableConcepts<ClaimResponseTotalCategory>([
      current.category,
    ]);

    if (!code) {
      return prev;
    }

    return {
      ...prev,
      [code]: mapFhirToMoney(MONEY_FACTOR, current.amount),
    };
  }, {});

  return {
    balanceOutstanding:
      splitTotals[ClaimResponseTotalCategory.BalanceOutstandingAmount],
    claimedAmount: splitTotals[ClaimResponseTotalCategory.TotalClaimedAmount],
    paidProvider:
      splitTotals[ClaimResponseTotalCategory.TotalPaidProviderAmount],
  };
};

export const getItemTotal = (
  adjudication: fhir4.ClaimResponseItemAdjudication[] = [],
): Partial<ClaimResponseItemTotal> => {
  const splitAdjudications = adjudication.reduce<
    Partial<
      Record<ClaimResponseItemAdjudicationCategory, ChargeItemDefinitionMoney>
    >
  >((prev, current) => {
    const code =
      getFirstCodeFromCodableConcepts<ClaimResponseItemAdjudicationCategory>([
        current.category,
      ]);

    if (!code) {
      return prev;
    }

    return {
      ...prev,
      [code]: mapFhirToMoney(MONEY_FACTOR, current.amount),
    };
  }, {});

  return {
    billedAmount:
      splitAdjudications[ClaimResponseItemAdjudicationCategory.BilledAmount],
    medicalAidPayable:
      splitAdjudications[
        ClaimResponseItemAdjudicationCategory.MedicalAidPayable
      ],
    medicalAidRate:
      splitAdjudications[ClaimResponseItemAdjudicationCategory.MedicalAidRate],
  };
};

const includes = (list?: (number | undefined)[], num?: number) =>
  list?.includes(num);
const defined = (num?: number): num is number => num !== undefined;

const getNotes = (
  noteNumbers: number[],
  notes: fhir4.ClaimResponseProcessNote[],
): string[] =>
  notes
    .filter(({ number: noteNumber }) => includes(noteNumbers, noteNumber))
    .map((note) => note.text);

type PartialClaimResponseItem =
  Required<ClaimResponseModelGetResponse>['items'][number];

const getItem = (
  item: fhir4.ClaimResponseItem,
  notes: fhir4.ClaimResponseProcessNote[] = [],
): PartialClaimResponseItem => {
  const noteNumbers = item.noteNumber || [];

  return {
    sequence: item.itemSequence,
    notes: getNotes(noteNumbers, notes),
    total: getItemTotal(item.adjudication),
  };
};

const getUnlinkedNotes = (
  items: fhir4.ClaimResponseItem[] = [],
  notes: fhir4.ClaimResponseProcessNote[] = [],
): string[] => {
  const itemNotes = items.map((item) => item.noteNumber || []).flat();
  const allNotes = notes.map((note) => note.number).filter(defined);

  const unlinkedNotes = allNotes.filter((note) => !includes(itemNotes, note));

  return getNotes(unlinkedNotes, notes);
};

export const mapFhirClaimResponseToModel = (
  fhir: WithId<fhir4.ClaimResponse>,
): ClaimResponseModelGetResponse => {
  const {
    patient,
    type,
    insurer,
    created,
    outcome,
    status,
    id,
    request,
    total,
    processNote,
    disposition,
    item = [],
  } = fhir;

  return {
    id,
    date: created,
    provider: getValidReference(insurer),
    claim: getValidReference(request),
    patient: getValidReference(patient),
    outcome,
    status,
    statusDescription: disposition,
    type: getFirstCodeFromCodableConcepts([type]),
    total: getTotal(total),
    notes: getUnlinkedNotes(item, processNote),
    items: item.map((i) => getItem(i, processNote)),
  };
};
